const SATS_PER_BTC = 100_000_000;

export function satsToBtc(sats: number): number {
  return sats / SATS_PER_BTC;
}

export function btcToSats(btc: number): number {
  return Math.round(btc * SATS_PER_BTC);
}

export function formatBtc(sats: number): string {
  const btc = satsToBtc(sats);
  return btc.toLocaleString('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });
}

export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatSignedSats(sats: number): string {
  const sign = sats > 0 ? '+' : sats < 0 ? '−' : '';
  return `${sign}${formatBtc(Math.abs(sats))} BTC`;
}

export interface CompactAmount {
  /** The full signed amount, e.g. "+0.00123456 BTC". */
  full: string;
  /** The first `digits` significant digits, with "…" when anything non-zero was cut. */
  compact: string;
  truncated: boolean;
}

/**
 * Shortens a signed BTC amount to its first `digits` significant digits
 * (counting from the first non-zero digit, so a small amount like
 * 0.00000546 isn't shown as all zeros), for less cluttered lists. Digits
 * are cut, never rounded, so the compact form is always an exact prefix of
 * the full one; trailing zeros that were cut don't count as truncation.
 */
export function compactSignedSats(sats: number, digits: number = 6): CompactAmount {
  const full = formatSignedSats(sats);
  const sign = sats > 0 ? '+' : sats < 0 ? '−' : '';
  const number = formatBtc(Math.abs(sats));

  let counted = 0;
  let seenNonZero = false;
  let cut = number.length;
  for (let i = 0; i < number.length; i++) {
    const ch = number[i];
    if (ch < '0' || ch > '9') continue;
    if (ch !== '0') seenNonZero = true;
    if (seenNonZero) counted++;
    if (counted === digits) {
      cut = i + 1;
      break;
    }
  }

  const rest = number.slice(cut);
  const truncated = /[1-9]/.test(rest);
  if (!truncated) return { full, compact: full, truncated: false };
  return { full, compact: `${sign}${number.slice(0, cut)}… BTC`, truncated: true };
}

/** Rounds to whole cents first, so a tiny loss shows as "$0.00", never "−$0.00". */
export function formatSignedUsd(value: number): string {
  const cents = Math.round(value * 100);
  const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
  return `${sign}${formatUsd(Math.abs(cents) / 100)}`;
}

/** -1, 0 or 1 for a dollar amount at cent precision, for choosing a gain/loss colour. */
export function centsSign(value: number): -1 | 0 | 1 {
  const cents = Math.round(value * 100);
  return cents > 0 ? 1 : cents < 0 ? -1 : 0;
}

export function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Pending';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Pending';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * A timestamp short enough to stay on one line beside an amount on a phone:
 * "Oct 6, 11:15 PM" this year, "Oct 6, 2025" for an older one (the exact
 * time is in the row's details).
 */
export function formatShortTimestamp(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'Pending';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Pending';
  if (date.getFullYear() !== now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatPercent(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return '—';
  return `${(fraction * 100).toFixed(digits)}%`;
}

export function formatSignedPercent(
  value: number | null | undefined,
  digits = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

export function formatRelativeAge(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'unknown';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const diffMs = now.getTime() - then;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.round(diffHour / 24);
  return `${diffDay}d ago`;
}
